import type { ResellerPermission } from "@acme/auth/permissions";
import type { FeatureFlags } from "@acme/db/schema";

import type { Session } from "./auth-server";

export interface RouteDefinition {
  path: string | RegExp | (RegExp | string)[];
  /**
   * "public" - available to everyone
   * "minimal" - available to authenticated users
   * AccessRule[] - available to authenticated users with selected org, and the required access level
   */
  accessConfig: AccessRule[] | "public" | "minimal";
}

type Feature = keyof FeatureFlags;
type NoFeature = `!${Feature}`;

export type AccessRule =
  | {
      features: (Feature | NoFeature)[];
    }
  | {
      minRole: UserRole;
      userType: "internal";
    }
  | {
      minRole: UserRole;
      userType: "reseller";
      /**
       * Optionally specify the permissions required for the route
       */
      permissions?: ResellerPermission[];
    };

export type UserRole = "org:member" | "org:admin" | "org:internal:support";
export type UserType = "reseller" | "internal";
export type AuthLevel = "public" | "minimal";

export const routeConfig: RouteDefinition[] = [
  /**
   * Public routes
   */
  {
    path: [
      "/api/trpc(.*)",
      "/favicon.ico",
      "/robots.txt",
      "/public(.*)",
      "/api/s3-proxy(.*)",
      "/api/public(.*)",
      "/api/internal(.*)",
      "/api/sms-gateway(.*)",
      "/api/impersonate",
      "/api/invitation(.*)",
      "/api/connect-mailbox(.*)",
      "/api/gmail/pubsub-push",
      "/sign-in(.*)",
      "/sign-up(.*)",
      "/handoff",
      "/forgot-password",
      "/reset-password",
      "/clear-session",
      "/accept-invitation",
      "/tos",
      "/banned",
      "/",
      "/privacy",
      "/impersonate-reseller(.*)",
      "/under-maintenance",
      "/.well-known(.*)",
    ],
    accessConfig: "public",
  },

  /**
   * Minimal routes
   */
  {
    path: [
      "/bugs",
      "/api/proxy-screenshot",
      "/select-org",
      "/account",
      "/unauthorized",
    ],
    accessConfig: "minimal",
  },

  /**
   * Other routes
   */
  {
    path: "/billing(.*)",
    accessConfig: [
      {
        minRole: "org:admin",
        userType: "internal",
      },
      {
        minRole: "org:admin",
        userType: "reseller",
      },
    ],
  },
  {
    path: [
      "/settings/carriers",
      "/settings/proxies",
      "/settings/tools(.*)",
      "/devices/models",
      "/devices/plans",
      "/devices/port-groups",
      "/devices/firmware-versions",
      "/vendors(.*)",
      "/lines(.*)",
    ],
    accessConfig: [
      {
        minRole: "org:admin",
        userType: "internal",
      },
    ],
  },
  {
    path: [
      "/tasks",
      "/lines",
      "/lines/sim-swaps",
      "/lines/ports",
      "/lines/add",
      "/orders",
      "/orders/topups",
      "/orders/topups(.*)",
      "/orders/(.*)",
    ],
    accessConfig: [
      {
        minRole: "org:member",
        userType: "internal",
      },
    ],
  },
  {
    path: ["/2fa"],
    accessConfig: [
      {
        minRole: "org:member",
        userType: "internal",
      },
    ],
  },
  {
    path: ["/lines(.*)", "/vendors(.*)"],
    accessConfig: [
      {
        features: ["nlm"],
      },
    ],
  },
  {
    path: ["/legacy/carrier-statements(.*)"],
    accessConfig: [
      {
        minRole: "org:admin",
        userType: "internal",
      },
      {
        features: ["!nlm"],
      },
    ],
  },
  {
    path: ["/devices/map"],
    accessConfig: [
      {
        minRole: "org:admin",
        userType: "reseller",
      },
      {
        minRole: "org:member",
        userType: "internal",
      },
    ],
  },
  {
    path: ["/devices/sn(.*)/networking"],
    accessConfig: [
      {
        permissions: ["rsl:networking-rules"],
        userType: "reseller",
        minRole: "org:member",
      },
      {
        minRole: "org:internal:support",
        userType: "internal",
      },
    ],
  },
  {
    path: [
      "/devices",
      /^\/devices\/sn\/[^/]+$/,
      /^\/devices\/sn\/[^/]+\/(?!networking).*$/,
      "/overview",
    ],
    accessConfig: [
      {
        minRole: "org:member",
        userType: "reseller",
      },
      {
        minRole: "org:internal:support",
        userType: "internal",
      },
    ],
  },
  {
    path: ["/devices(.*)"],
    accessConfig: [
      {
        features: ["deviceManagement"],
      },
    ],
  },
  {
    path: ["/resellers(.*)"],
    accessConfig: [
      {
        minRole: "org:member",
        userType: "internal",
      },
      {
        minRole: "org:admin",
        userType: "reseller",
        permissions: ["rsl:sub-resellers"],
      },
    ],
  },
  {
    path: [/^\/resellers\/([^/]+)\/(devices|plans|port-groups)$/],
    accessConfig: [
      {
        features: ["deviceManagement"],
      },
    ],
  },

  /**
   * Settings routes
   */
  {
    path: ["/settings/organization"],
    accessConfig: [
      {
        minRole: "org:admin",
        userType: "internal",
      },
      {
        minRole: "org:admin",
        userType: "reseller",
      },
    ],
  },
  {
    path: [
      "/settings/api-access",
      "/settings/device-config",
      "/settings/notifications",
      "/settings/users",
    ],
    accessConfig: [
      {
        minRole: "org:admin",
        userType: "reseller",
      },
    ],
  },
  {
    path: [
      "/settings/announcement",
      "/settings/billing",
      "/settings/device-config",
      "/settings/tacs",
      "/settings/tags",
      "/settings/users",
      "/settings/2fa-mailboxes",
    ],
    accessConfig: [
      {
        minRole: "org:admin",
        userType: "internal",
      },
    ],
  },
  {
    path: ["/settings/2fa-mailboxes"],
    accessConfig: [
      {
        features: ["connectedMailboxes"],
      },
    ],
  },
  {
    path: ["/settings/tags"],
    accessConfig: [
      {
        features: ["nlm"],
      },
    ],
  },
  {
    path: ["/settings/device-config"],
    accessConfig: [
      {
        features: ["deviceManagement"],
      },
    ],
  },
  {
    path: ["/settings/billing"],
    accessConfig: [
      {
        features: ["nlm"],
      },
    ],
  },
  {
    path: ["/settings"],
    accessConfig: [
      {
        minRole: "org:admin",
        userType: "internal",
      },
      {
        minRole: "org:admin",
        userType: "reseller",
      },
    ],
  },
];

export function createPathMatcher(patterns: (string | RegExp)[]) {
  return function isPathMatch(pathname: string) {
    return patterns.some((pattern) => {
      if (pattern instanceof RegExp) {
        return pattern.test(pathname);
      }

      // Convert route pattern to regex
      const regexPattern = pattern
        // 1. Escape ALL special regex characters
        .replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
        // 2. Specifically unescape the (.*) pattern
        .replace(/\\\(\\\.\\\*\\\)/g, "(.*)")
        // 3. Add regex boundaries
        .replace(/^/, "^")
        .replace(/$/, "$");

      const regex = new RegExp(regexPattern);
      return regex.test(pathname);
    });
  };
}

const isPublicRoute = createPathMatcher(
  routeConfig
    .filter((route) => route.accessConfig === "public")
    .map((route) => route.path)
    .flat(),
);

const isMinimalRoute = createPathMatcher(
  routeConfig
    .filter((route) => route.accessConfig === "minimal")
    .map((route) => route.path)
    .flat(),
);

const isDefinedRoute = createPathMatcher(
  routeConfig.map((route) => route.path).flat(),
);

export type RoutePermissions =
  | {
      allowed: true;
    }
  | {
      allowed: false;
      redirect:
        | "/not-found"
        | "/unauthorized"
        | "/select-org"
        | "/sign-in"
        | "/banned";
    };

/**
 * Remove search params, leaving only the actual pathname
 */
export function normalizePathname(pathname: string) {
  return pathname.split("?")[0] ?? pathname;
}

/**
 * Validate a post-login redirect target. Only allow same-origin paths and
 * reject anything that would loop back to an auth route.
 */
export function getSafeRedirect(
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  if (!value.startsWith("/")) return null;
  // Reject protocol-relative or backslash tricks
  if (value.startsWith("//") || value.startsWith("/\\")) return null;
  // Strip both query and fragment before comparing against auth-route names —
  // otherwise `/sign-in#x` would bypass the loop check.
  const pn = normalizePathname(value).split("#")[0] ?? value;
  if (
    pn === "/sign-in" ||
    pn.startsWith("/sign-in/") ||
    pn === "/sign-up" ||
    pn.startsWith("/sign-up/") ||
    pn === "/select-org" ||
    pn === "/handoff" ||
    pn === "/clear-session" ||
    pn === "/forgot-password" ||
    pn === "/reset-password" ||
    pn === "/accept-invitation"
  ) {
    return null;
  }
  return value;
}

export function checkRouteAccess(
  pathname: string,
  userAuth: Session | null,
): RoutePermissions {
  const pn = normalizePathname(pathname);
  if (isPublicRoute(pn)) {
    return {
      allowed: true,
    };
  }

  if (userAuth?.userType === "reseller" && userAuth.bannedAt) {
    return {
      allowed: false,
      redirect: "/banned",
    };
  }

  if (!isDefinedRoute(pn)) {
    return {
      allowed: false,
      redirect: "/not-found",
    };
  }

  // If user is not authenticated, redirect to sign in
  if (!userAuth) {
    return {
      allowed: false,
      redirect: "/sign-in",
    };
  }

  // For intermediate users, only allow minimal auth routes
  if (isMinimalRoute(pn)) {
    return {
      allowed: true,
    };
  }

  if (userAuth.userType === "intermediate") {
    return {
      allowed: false,
      redirect: "/unauthorized",
    };
  }

  // find all config routes that match the pathname
  const matchingRoutes = routeConfig.filter((route) => {
    const path = Array.isArray(route.path) ? route.path : [route.path];
    const isPathMatch = createPathMatcher(path)(pn);
    return isPathMatch;
  });

  const requiredFeatures = matchingRoutes
    .flatMap((route) => {
      const accessConfig = route.accessConfig;
      if (Array.isArray(accessConfig)) {
        return accessConfig
          .filter((access) => "features" in access)
          .map((access) => access.features);
      }
      return [];
    })
    .flat();

  const hasRequiredFeatures = requiredFeatures.every((feature) => {
    if (feature.startsWith("!")) {
      return !userAuth.tenant.features[feature.slice(1) as Feature];
    }
    return userAuth.tenant.features[feature as Feature];
  });

  if (!hasRequiredFeatures) {
    return {
      allowed: false,
      redirect: "/unauthorized",
    };
  }

  // Get all routes that match the user type and role
  const isAuthorized = matchingRoutes.some((route) => {
    // Skip public and minimal routes
    if (route.accessConfig === "public" || route.accessConfig === "minimal") {
      return false;
    }

    const hasValidAccess = route.accessConfig.some((access) => {
      if ("features" in access) {
        return false; // features are checked above
      }

      // Check if user type matches
      const hasMatchingUserType = access.userType === userAuth.userType;

      // Check role requirements
      const hasRequiredRole =
        access.minRole === "org:internal:support" ||
        (access.minRole === "org:member" &&
          (userAuth.role === "org:member" || userAuth.role === "org:admin")) ||
        (access.minRole === "org:admin" && userAuth.role === "org:admin");

      // Check permissions if they exist
      const hasRequiredPermissions =
        !("permissions" in access) ||
        access.permissions?.every((permission) =>
          userAuth.permissions.includes(permission),
        );

      return hasMatchingUserType && hasRequiredRole && hasRequiredPermissions;
    });

    return hasValidAccess;
  });

  return isAuthorized
    ? { allowed: true }
    : { allowed: false, redirect: "/unauthorized" };
}
